<template>
    <div class="container">
        <transition name="modal-fade" appear>
            <div class="modal-backdrop" @click.self="close">
                <div class="modal" role="dialog" aria-modal="true" aria-labelledby="impersonate-modal-title" tabindex="-1" ref="modalEl" @keydown.tab="trapFocus" @keydown.esc="close">
                    <header class="modal-header">
                        <h5 class="modal-title" id="impersonate-modal-title">Impersonate Account</h5>
                        <button type="button" class="btn-close" aria-label="Close" @click="close"></button>
                    </header>
                    <section class="modal-body mx-2 mx-md-5">
                        <div class="d-flex flex-column flex-md-row justify-content-between gap-3">
                            <div>
                                <img 
                                    :src="`https://apps.uvu.edu/idphoto/${selected.uvid}`" 
                                    alt="Profile Photo" 
                                    width="150"
                                    @error="setAltImg"
                                >
                                <!-- Display person type badge -->
                                <div v-if="selected.person_type" class="mt-2">
                                    <span 
                                        class="badge" 
                                        :class="person_type_badge_class"
                                    >
                                        {{ person_type_display }}
                                    </span>
                                </div>
                            </div>
                            <div class="d-flex flex-column w-100">
                                <div class="autocomplete w-100">
                                    <div class="input-group">
                                        <span class="input-group-text">
                                            <div 
                                                class="spinner-border text-primary spinner-border-sm" 
                                                role="status" 
                                                v-if="search_loading"
                                            >
                                                <span class="visually-hidden">Loading...</span>
                                            </div>
                                            <svg v-if="!search_loading" width="1.5em" height="1.5em" viewBox="0 0 24 24">
                                                <circle cx="10" cy="8" r="4" fill="currentColor"></circle>
                                                <path fill="currentColor" d="M10.35 14.01C7.62 13.91 2 15.27 2 18v1c0 .55.45 1 1 1h8.54c-2.47-2.76-1.23-5.89-1.19-5.99zm9.08 4.01c.47-.8.7-1.77.48-2.82c-.34-1.64-1.72-2.95-3.38-3.16c-2.63-.34-4.85 1.87-4.5 4.5c.22 1.66 1.52 3.04 3.16 3.38c1.05.22 2.02-.01 2.82-.48l1.86 1.86a.996.996 0 1 0 1.41-1.41l-1.85-1.87zM16 18c-1.1 0-2-.9-2-2s.9-2 2-2s2 .9 2 2s-.9 2-2 2z"></path>
                                            </svg>
                                        </span>
                                        <input
                                            v-model="search_filter"
                                            @input="search_accounts"
                                            @focus="search_filter_active = true"
                                            @keydown="handle_search_keydown"
                                            :placeholder="`Search by UVID or Name (Students & Employees)`"
                                            class="form-control"
                                            type="text"
                                            role="combobox"
                                            aria-autocomplete="list"
                                            aria-haspopup="listbox"
                                            :aria-expanded="search_filter_active"
                                            aria-controls="impersonate-search-listbox"
                                            :aria-activedescendant="active_index >= 0 ? `impersonate-search-option-${active_index}` : undefined"
                                            :disabled="isImpersonationSet"
                                        />
                                        <div class="input-group-append">
                                            <button 
                                                class="btn btn-secondary rounded-0" 
                                                type="button" 
                                                :disabled="!valid_selected_person" 
                                                @click="set_impersonate()" 
                                                v-if="!sessionStore.isImpersonationSet"
                                            >
                                                Impersonate
                                            </button>
                                            <button 
                                                class="btn btn-secondary rounded-0" 
                                                type="button" 
                                                :disabled="!valid_selected_person" 
                                                @click="remove_impersonate()" 
                                                v-else
                                            >
                                                Remove Impersonation
                                            </button>
                                        </div>
                                    </div>
                                    <ul
                                        v-show="search_filter_active"
                                        id="impersonate-search-listbox"
                                        role="listbox"
                                        class="autocomplete-results"
                                        :class="{'autocomplete-results-border': search_results.length > 0}"
                                    >
                                        <li
                                            v-for="(result, i) in search_results"
                                            :key="i"
                                            :id="`impersonate-search-option-${i}`"
                                            role="option"
                                            :aria-selected="i === active_index"
                                            tabindex="-1"
                                            class="autocomplete-result d-flex justify-content-start align-items-center"
                                            :class="{'active' : i === active_index}"
                                            @click="select_person(result)"
                                        >
                                            <img
                                                :src="`https://apps.uvu.edu/idphoto/${result.img_id}?resize=40x40`"
                                                :alt="result.name"
                                                @error="setAltImg"
                                            />
                                            <span class="ms-2 d-flex flex-column justify-content-center align-items-start result-info">
                                                <span class="border-bottom">
                                                    {{ result.name }} ({{ result.uvid }})
                                                    <span 
                                                        class="badge badge-sm ms-1" 
                                                        :class="result.person_type === 'employee' ? 'bg-primary' : 'bg-success'"
                                                    >
                                                        {{ result.person_type }}
                                                    </span>
                                                </span>
                                                <span class="font-12 text-muted">
                                                    {{ result.display_subtitle }}
                                                </span>
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import _ from 'lodash'
import { useSessionStore } from '@/stores/SessionStore'
import { trapTabKey } from '@/utils/focusTrap'
import { defineComponent, onMounted } from 'vue';

export default defineComponent({
    setup() {
        const sessionStore = useSessionStore()
        
        onMounted(async () => {})
        
        return { sessionStore }
    },

    components: {},
    
    data() {
        return {
            selected: {},
            search_filter: '',
            search_filter_active: false,
            search_results: [],
            search_loading: false,
            active_index: -1,
        }
    },

    async mounted() {
        this.triggerEl = document.activeElement
        this.$nextTick(() => { this.$refs.modalEl?.focus() })
        // Load existing impersonation if set
        if (this.sessionStore.isImpersonationSet) {
            this.select_person(this.sessionStore.getImpersonation)
        }
    },
    
    props: {},
    
    methods: {
        trapFocus(event) {
            trapTabKey(event, this.$refs.modalEl)
        },

        handle_search_keydown(e) {
            const results = this.search_results
            if (e.key === 'Escape') {
                this.search_filter_active = false
                this.active_index = -1
                return
            }
            if (!this.search_filter_active || results.length === 0) return

            if (e.key === 'ArrowDown') {
                e.preventDefault()
                this.active_index = (this.active_index + 1) % results.length
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                this.active_index = (this.active_index - 1 + results.length) % results.length
            } else if (e.key === 'Enter' && this.active_index >= 0) {
                e.preventDefault()
                this.select_person(results[this.active_index])
            }
        },

        async set_impersonate() {
            const selected = { ...this.selected }
            console.log(`selected: ${selected}`)
            await this.sessionStore.setImpersonation(selected)
            this.$router.go()
        },

        remove_impersonate() {
            this.selected = {}
            this.search_filter = ''
            this.sessionStore.setImpersonation(this.selected)
            this.$router.go()
        },

        search_accounts: _.debounce(async function() {
            this.selected = {}
            this.search_results = []
            this.search_filter_active = true

            // Only call search API if more than two characters
            if (this.search_filter.length < 3) {
                this.search_results = []
                return
            }
            
            this.search_loading = true
            
            // Use the new unified person-search endpoint
            await this.sessionStore.request_data(
                'get', 
                `/person-search?search_str=${this.search_filter}`, 
                'Person Search'
            )
                .then(data => this.search_results = Object.values(data))
            
            this.search_loading = false
        }, 500),

        select_person(person) {
            this.selected = { ...person }
            this.search_filter_active = false
            this.active_index = -1
            this.search_filter = `${this.selected.name} (${this.selected.uvid})`
        },

        focusout_autocomplete() {
            // Have to use an interval to handle when a record is selected
            setTimeout(() => {
                this.search_filter_active = (!this.selected)
            }, 3000)
        },
        
        setAltImg(event) {
            event.target.src = require('@/assets/images/default.jpg')
        },

        close() {
            this.$emit('close')
            this.triggerEl?.focus()
        },
    },

    computed: {
        valid_selected_person() {
            const selected = this.selected
            if (!selected.uvid || !selected.name) return false
            return true
        },
        
        isImpersonationSet() {
            return this.sessionStore.isImpersonationSet
        },
        
        person_type_display() {
            return this.selected.person_type 
                ? this.selected.person_type.charAt(0).toUpperCase() + this.selected.person_type.slice(1)
                : ''
        },
        
        person_type_badge_class() {
            return this.selected.person_type === 'employee' 
                ? 'bg-primary' 
                : 'bg-success'
        }
    }
})
</script>

<style scoped lang="scss">
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
}

.modal {
    margin: 0;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #FFFFFF;
    box-shadow: 2px 2px 20px 1px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    height: auto;
    max-height: 85vh;
    width: min(95vw, 850px);
}

@import 'https://fonts.googleapis.com/css2?family=Material+Icons';

.material-icons {
    vertical-align: middle;
    margin-right: 5px;
}

.autocomplete {
    position: relative;
}

.autocomplete-results {
    padding: 0;
    margin: 0;
    max-height: 20em;
    overflow: auto;
    z-index: 10000;
    left: 0;
    right: 0;
    position: absolute;
}

.autocomplete-results-border {
    border: .5px solid #615a5a;
}

.autocomplete-result {
    list-style: none;
    text-align: left;
    padding: 4px 2px;
    cursor: pointer;
    background: #fff;
}

.autocomplete-result:hover {
    background-color: #7a8280;
    color: white;
}

.autocomplete-result.active {
    background-color: #7a8280;
    color: white;
}

.result-info {
    line-height: 90%;
}

.badge-sm {
    font-size: 0.7em;
    padding: 0.2em 0.4em;
}

.font-12 {
    font-size: 12px;
}
</style>